// src/api/r2.js

// === Outbox per invii offline/temporaneamente falliti ===
const OUTBOX_KEY = "RB_OUTBOX_V1";

/** Salva un evento/oggetto sul backend (Pages Function /api/drive).
 *  Se fallisce, accoda in localStorage e ritorna { ok:false, queued:true }.
 */
export async function saveToR2(kind, data = {}) {
  // prendo l'utente per valorizzare user.id/email
  const { User } = await import("@/api/entities");
  const u = await User.me();
  const payload = {
    kind,
    user: { id: u?.id, email: u?.email, name: u?.full_name || u?.name },
    ...data,
  };

  try {
    const res = await fetch("/api/drive", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
    return await res.json(); // { ok:true, key:'...' }
  } catch (e) {
    const q = JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]");
    q.push({ item: payload, when: Date.now() });
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(q));
    return { ok: false, queued: true, error: String(e) };
  }
}

/** Svuota la coda locale riprovando gli invii su /api/drive. */
export async function flushOutbox() {
  const q = JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]");
  if (!q.length) return;
  const next = [];
  for (const { item } of q) {
    try {
      const res = await fetch("/api/drive", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(item),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
    } catch {
      next.push({ item, when: Date.now() });
    }
  }
  localStorage.setItem(OUTBOX_KEY, JSON.stringify(next));
}

// ---------------------------------------------------------------------
// >>> SALVATAGGIO LEZIONI (telemetria / eventi) <<<
// Crea un record stile esercizi in R2:
//   users/<uid>/lessons/<lesson_id>/<ISO>.json
// con kind: "lesson_event" e payload dettagliato.
// Lato server /api/drive useremo 'kind' per instradare la scrittura.
// ---------------------------------------------------------------------

/**
 * Salva un evento di lezione su R2 (con outbox offline).
 * @param {Object} options
 *  - user    : opzionale; se assente lo ricava da User.me() dentro saveToR2
 *  - lesson  : record della lezione (id, title, order, ecc.)
 *  - event   : oggetto con metriche/telemetria (es. { type, active_seconds, video_* })
 *
 * Esempi di `event.type`: "start" | "heartbeat" | "pause" | "resume" | "ended" | "complete" | "mark_completed"
 */
export async function saveLessonEvent({ user, lesson, event }) {
  const ts = new Date().toISOString();

  const payload = {
    // routing lato server simile a "exercise_result"
    lesson_id: lesson?.id || null,
    lesson_title: lesson?.title || null,
    lesson_order: lesson?.order ?? null,
    now: ts,

    // dati “grezzi” dell’evento (telemetria)
    data: {
      ...event,
    },

    // qualche meta utile per debug/analisi
    meta: {
      source: "lesson_viewer",
      ua: navigator.userAgent,
      app_ver:
        (import.meta && import.meta.env && import.meta.env.VITE_APP_VERSION) ||
        null,
    },
  };

  // Il backend /api/drive scriverà la chiave:
  // users/<uid>/lessons/<lesson_id>/<ts>.json
  return saveToR2("lesson_event", payload);
}

// ---------------------------------------------------------------------
// >>> SNAPSHOT PROGRESSI UTENTE <<<
// Salva uno snapshot compatto dei progressi su R2 dopo il Check-in:
//   - users/<uid>/stats/progress/latest.json
//   - users/<uid>/stats/progress/daily/YYYY-MM/YYYY-MM-DD.json  (lato server)
// ---------------------------------------------------------------------

export async function saveUserProgressSnapshot() {
  const { User, DailyCheckIn, UserProgress } = await import("@/api/entities");

  const u = await User.me();
  const me = {
    id: u?.id || u?.uid || "unknown",
    email: u?.email || "",
    name: u?.full_name || u?.name || "",
  };

  // Carico dati necessari
  const [checkInsRaw, userProgRaw] = await Promise.all([
    DailyCheckIn.filter({ created_by: me.email }, "-date").catch(() => []),
    UserProgress.filter({ created_by: me.email }).catch(() => []),
  ]);

  // Normalizzazione minima check-in
  const norm = (ci) => {
    if (!ci) return null;
    const dateSrc = ci.date || ci.day || ci.created_at || ci.now;
    const date = String(dateSrc || "").slice(0, 10); // YYYY-MM-DD
    const raw = ci.status || ci.q3_breathing || (ci.relapse ? "relapse" : "");
    const status = /relapse|fail|ricad/i.test(raw)
      ? "relapse"
      : /skip|none/i.test(raw)
      ? "skip"
      : "success";
    return {
      ...ci,
      date,
      status,
      q3_breathing: ci.q3_breathing ?? status,
      relapse_cigarettes_smoked: Number(ci.relapse_cigarettes_smoked || 0),
    };
  };

  const checkIns = (Array.isArray(checkInsRaw) ? checkInsRaw : [])
    .map(norm)
    .filter(Boolean)
    .sort((a, b) => a.date.localeCompare(b.date));

  const userProgress = Array.isArray(userProgRaw) ? userProgRaw : [];

  // Totali coerenti con Progress.jsx (versione compatta)
  const today = new Date();
  const startDate = checkIns.length
    ? new Date(checkIns[0].date + "T12:00:00")
    : today;

  let cigarettesAvoided = 0;
  let totalSavings = 0;

  const map = new Map(checkIns.map((ci) => [ci.date, ci]));
  const yearsPassedFrom = (d) => d.getFullYear() - startDate.getFullYear();

  for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
    const ds = d.toISOString().slice(0, 10);
    const day = map.get(ds);
    const y = yearsPassedFrom(d);

    let dailyEq = 0;
    let dailyCost = 0;

    if (u.smoking_type === "cigarettes") {
      const perDay = Number(u.cigarettes_per_day || 0);
      const currentPackPrice = Number(u.pack_price || 0) * Math.pow(1.03, y);
      const perPack = Number(u.cigarettes_per_pack || 20) || 20;
      const pricePerCig = perPack > 0 ? currentPackPrice / perPack : 0;
      dailyEq = perDay;
      dailyCost = perDay * pricePerCig;
    } else if (u.smoking_type === "tobacco") {
      const wt = Number(u.tobacco_packs_per_week || 0);
      const wp = Number(u.paper_packs_per_week || 0);
      const wf = Number(u.filter_packs_per_week || 0);
      const dailyTobaccoCost =
        (wt * Number(u.tobacco_pack_price || 0) * Math.pow(1.03, y)) / 7;
      const dailyPaperCost =
        (wp * Number(u.paper_pack_price || 0) * Math.pow(1.03, y)) / 7;
      const dailyFilterCost =
        (wf * Number(u.filter_pack_price || 0) * Math.pow(1.03, y)) / 7;
      dailyCost = dailyTobaccoCost + dailyPaperCost + dailyFilterCost;
      dailyEq = ((u.tobacco_packs_per_week || 0) * 100) / 7; // stima eq/sig
    }

    const dailyLighterCost =
      ((Number(u.lighters_per_week || 0)) * Math.pow(1.03, y)) / 7;
    dailyCost += dailyLighterCost;

    const smoked =
      day && day.q3_breathing === "relapse"
        ? Number(day.relapse_cigarettes_smoked || 0)
        : 0;
    const pricePerEq = dailyEq > 0 ? dailyCost / dailyEq : 0;

    cigarettesAvoided += dailyEq - smoked;
    totalSavings += dailyCost - smoked * pricePerEq;
  }

  cigarettesAvoided = Math.max(0, Math.round(cigarettesAvoided));
  totalSavings = Math.max(0, Math.round(totalSavings));

  // Streak, achievements e prossimi obiettivi
  const periods = [
    { d: { days: 1 }, label: "1 Giorno" },
    { d: { days: 3 }, label: "3 Giorni" },
    { d: { days: 7 }, label: "1 Settimana" },
    { d: { days: 14 }, label: "2 Settimane" },
    { d: { months: 1 }, label: "1 Mese" },
    { d: { months: 2 }, label: "2 Mesi" },
    { d: { months: 6 }, label: "6 Mesi" },
    { d: { years: 1 }, label: "1 Anno" },
    { d: { years: 2 }, label: "2 Anni" },
    { d: { years: 5 }, label: "5 Anni" },
  ];
  const addDur = (base, delta) => {
    const x = new Date(base);
    if (delta.days) x.setDate(x.getDate() + delta.days);
    if (delta.months) x.setMonth(x.getMonth() + delta.months);
    if (delta.years) x.setFullYear(x.getFullYear() + delta.years);
    return x;
  };

  // ultima ricaduta
  const lastRelapseIndex = checkIns
    .map((ci) => ci.q3_breathing === "relapse")
    .lastIndexOf(true);
  let startGoals = today;
  if (checkIns.length) {
    if (lastRelapseIndex === -1) {
      startGoals = new Date(checkIns[0].date + "T12:00:00");
    } else {
      const firstAfter = checkIns.find(
        (ci, idx) => idx > lastRelapseIndex && ci.q3_breathing !== "relapse",
      );
      startGoals = firstAfter
        ? new Date(firstAfter.date + "T12:00:00")
        : today;
    }
  }

  let streak = 0;
  let longestStreak = 0;
  const achievements = new Set();

  checkIns.forEach((ci, idx) => {
    if (ci.q3_breathing === "relapse") {
      streak = 0;
    } else if (idx > lastRelapseIndex) {
      streak++;
      if (streak > longestStreak) longestStreak = streak;
    } else {
      streak = 0;
    }
    if (streak >= 1) achievements.add("1 Giorno");
    if (streak >= 3) achievements.add("3 Giorni");
    if (streak >= 7) achievements.add("1 Settimana");
    if (streak >= 14) achievements.add("2 Settimane");
    if (streak >= 30) achievements.add("1 Mese");
    if (streak >= 60) achievements.add("2 Mesi");
  });

  // Ricompense personalizzate (lezione 6) se presenti
  const l6 = userProgress.find(
    (p) => p.exercise_completed && p.exercise_answer && p.lesson_id?.includes("6"),
  );
  const rewards = (() => {
    try {
      if (
        typeof l6?.exercise_answer === "string" &&
        l6.exercise_answer.trim().startsWith("{")
      ) {
        return JSON.parse(l6.exercise_answer);
      }
    } catch {}
    return null;
  })();

  const upcomingGoals = periods
    .map((p) => {
      const date = addDur(startGoals, p.d);
      let label = p.label;
      let reward = null;
      if (rewards) {
        const map = {
          "1 Giorno": "ricompensa_1_giorno",
          "3 Giorni": "ricompensa_3_giorni",
          "1 Settimana": "ricompensa_1_settimana",
          "2 Settimane": "ricompensa_2_settimane",
          "1 Mese": "ricompensa_1_mese",
          "2 Mesi": "ricompensa_2_mesi",
          "6 Mesi": "ricompensa_6_mesi",
          "1 Anno": "ricompensa_1_anno",
          "2 Anni": "ricompensa_2_anni",
          "5 Anni": "ricompensa_5_anni",
        };
        const k = map[p.label];
        if (k && rewards[k]) reward = rewards[k];
      }
      return { date: date.toISOString(), label, reward };
    })
    .filter((g) => new Date(g.date) > today)
    .sort((a, b) => a.date.localeCompare(b.date));

  const snapshot = {
    kind: "user_stats_progress",
    now: new Date().toISOString(),
    user: me,
    totals: {
      cigarettes_avoided: cigarettesAvoided,
      savings_eur: totalSavings,
      longest_streak: longestStreak,
    },
    upcoming_goals: upcomingGoals.slice(0, 12),
    achieved_goals: Array.from(achievements),
  };

  // Il backend scriverà latest + daily
  await saveToR2("user_stats_progress", { snapshot });
}

// === Helpers per MEDIA in R2 (usati dall'admin per allegare audio_url) ===

/** Ritorna la lista delle chiavi in R2 con un dato prefisso.
 *  Richiede la Pages Function /api/r2-list (binding a RB_BUCKET lato server).
 *  Output: array di stringhe (chiavi R2) — in fallback normalizza altri formati.
 */
export async function list(prefix = "") {
  const url = `/api/r2-list?prefix=${encodeURIComponent(prefix)}&ts=${Date.now()}`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`r2/list ${res.status}`);

  const data = await res.json();
  if (Array.isArray(data)) return data;            // già array di chiavi
  if (Array.isArray(data?.keys)) return data.keys; // { keys: [...] }

  const objs = data?.objects || data?.results || data?.Contents || [];
  return objs
    .map(it => (typeof it === "string" ? it : it?.key || it?.Key || it?.name))
    .filter(Boolean);
}

/** Variante compatibile che restituisce oggetti tipo { key } */
export async function listObjects(prefix = "") {
  const keys = await list(prefix);
  return keys.map(key => ({ key }));
}

/** Costruisce l’URL pubblico per una chiave nel bucket.
 *  Passiamo dalla Pages Function /api/media?key=... (supporta Range/HEAD).
 */
export function publicUrl(key) {
  return `/api/media?key=${encodeURIComponent(key)}`;
}

/** Facoltativo: controlla che l’URL esista con una HEAD */
export async function head(urlOrKey) {
  const url = urlOrKey.includes("://") ? urlOrKey : publicUrl(urlOrKey);
  const r = await fetch(url, { method: "HEAD" });
  return r.ok;
}

export default {
  saveToR2,
  flushOutbox,
  saveLessonEvent,
  saveUserProgressSnapshot, // <-- nuova export
  list,
  listObjects,
  publicUrl,
  head,
};
